import { useEffect, useState } from "react";
import { Eraser, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, fmtWhen } from "@/lib/api";

/** The one-time clearing of every unit's use cases, before the units upload their first template.
 *  Only the two SDTD administrators reach it, and the portal allows it exactly once. */
export const FlushAll = ({ onDone }) => {
  const [state, setState] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const read = () =>
    api
      .get("/admin/flush-all")
      .then((r) => setState(r.data))
      .catch(() => setState(null));

  useEffect(() => {
    read();
  }, []);

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/flush-all", { confirm, reason });
      toast.success(
        `Cleared — ${data.removed.use_cases} use case(s) and ${data.removed.readings} reading(s) ` +
          "removed across every unit. The units can now make their first fill.",
      );
      setState(data.now);
      setConfirm("");
      setReason("");
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const phrase = state.phrase || "CLEAR ALL PROJECTS";

  return (
    <div
      className="mt-8 border border-[hsl(var(--at-risk)/0.5)] bg-card p-5"
      data-testid="flush-all"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--at-risk))]" />
        <div className="max-w-3xl">
          <div className="label-caps text-[hsl(var(--at-risk))]">
            SDTD administrator · one time only
          </div>
          <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
            Clear every unit's use cases, once
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Meant for the moment before the units upload their filled templates for the first time:
            it empties the use cases of all thirteen units, their journeys, their milestone dates,
            their trial readings and everything waiting for a SPOC, and puts every unit back on its
            first fill. Accounts, the team directory, the training records and the stored workbooks
            are left exactly as they are. The portal allows this once — after that a unit can still
            be cleared one at a time below.
          </p>
        </div>
      </div>

      {state.used ? (
        <div
          className="mt-4 border border-border bg-muted p-4 text-xs"
          data-testid="flush-all-used"
        >
          <div className="label-caps">Already used</div>
          <p className="mt-1 text-muted-foreground">
            {state.used_by} used the one-time clearing on {fmtWhen(state.used_at)} —{" "}
            {state.used_removed?.use_cases ?? 0} use case(s) and{" "}
            {state.used_removed?.readings ?? 0} reading(s) were removed.
            {state.reason ? ` “${state.reason}”` : ""} A single unit can still be cleared below.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid gap-px bg-border sm:grid-cols-3 lg:grid-cols-6" data-testid="flush-all-preview">
            {[
              ["Use cases", state.use_cases, true],
              ["Trial readings", state.readings, true],
              ["Compliance snapshots", state.snapshots, true],
              ["Waiting for a SPOC", state.pending_awaiting, true],
              ["Accounts (kept)", state.accounts, false],
              ["Team members (kept)", state.team_members, false],
            ].map(([label, value, goes]) => (
              <div key={label} className="bg-card px-4 py-3">
                <div
                  className="font-data text-lg font-bold"
                  style={goes ? { color: "hsl(var(--at-risk))" } : undefined}
                >
                  {value ?? 0}
                </div>
                <div className="font-data mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="font-data mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            {(state.units || []).map((u) => (
              <span key={u.unit} data-testid={`flush-all-unit-${u.unit}`}>
                {u.unit} {u.use_cases}
                {u.first_fill_done ? " · first fill done" : ""}
              </span>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-start gap-3">
            <Input
              className="h-11 w-[260px] rounded-none font-data"
              placeholder={`Type ${phrase}`}
              data-testid="flush-all-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Textarea
              rows={1}
              className="w-[320px] rounded-none text-xs"
              placeholder="Why (goes on the activity trail)"
              data-testid="flush-all-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              className="h-11 rounded-none bg-[hsl(var(--at-risk))] font-display text-xs font-bold uppercase tracking-widest text-white hover:bg-[hsl(var(--at-risk)/0.85)]"
              data-testid="flush-all-submit"
              disabled={busy || confirm.trim().toUpperCase() !== phrase}
              onClick={go}
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Eraser className="mr-2 h-4 w-4" />
              )}
              Clear every unit
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
